// TUT-444 — tipos dos documentos do processo: versões, publicação, retenção e envio.
import type { EcoDePaginacao, ProcessStatus } from "../types";

export type DocumentCategoryKey =
  | "pleading"
  | "expert_report"
  | "technical_report"
  | "evidence"
  | "correspondence"
  | "other";
export interface DocumentVersion {
  id: string;
  versionNumber: number;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  checksumSha256: string;
  status: ProcessStatus;
  /** TUT-041: por que a versão foi reprovada (nome da detecção do antivírus). */
  rejectionReason: string | null;
  author: string;
  createdAt: string;
}
/**
 * Publicação com cadeia por classe (TUT-043): laudo e peça revisam antes de
 * aprovar; as demais vão direto à aprovação da administração.
 */
export type DocumentPublicationStatus =
  | "AWAITING_REVIEW"
  | "AWAITING_APPROVAL"
  | "CHANGES_REQUESTED"
  | "PUBLISHED"
  | "DELIVERED";
export interface DocumentPublication {
  id: string;
  documentId: string;
  versionId: string;
  status: DocumentPublicationStatus;
  authorUserId: string | null;
  reviewerUserId: string | null;
  approverUserId: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
/**
 * TUT-489b — a cor da etiqueta é um TOM de situação da casa (o selo é o
 * `statusBadgeClass` do tom), espelho de `CORES_DE_ETIQUETA` da API.
 */
export const CORES_DE_ETIQUETA = ["critico", "atencao", "info", "ok", "neutro"] as const;
export type CorDeEtiqueta = (typeof CORES_DE_ETIQUETA)[number];
/** A etiqueta como o documento a carrega. */
export interface EtiquetaDoDocumento {
  id: string;
  nome: string;
  cor: CorDeEtiqueta;
}
/** A etiqueta do catálogo do escritório. */
export interface EtiquetaDoCatalogo extends EtiquetaDoDocumento {
  /** Veio com o escritório (a lista padrão); a removida não volta sozinha. */
  padrao: boolean;
  ativa: boolean;
}
export interface ProcessDocument {
  id: string;
  processId: string;
  processNumber: string;
  claimant: string;
  title: string;
  category: { key: DocumentCategoryKey; label: string };
  retention: { key: string; label: string };
  confidentiality: { key: string; label: string };
  /** TUT-044: motivo da guarda legal — presente = documento travado. */
  legalHoldReason: string | null;
  versionCount: number;
  latestVersion: DocumentVersion;
  versions: DocumentVersion[];
  /** TUT-489b — as etiquetas ativas, na ordem do catálogo. */
  tags: EtiquetaDoDocumento[];
  createdAt: string;
  updatedAt: string;
  /** TUT-013c: o conteúdo já existia e foi reaproveitado (resposta do complete). */
  storageReused?: boolean;
}
/**
 * TUT-489a — o status que o filtro da central aceita: a situação da versão
 * atual (antivírus) e a guarda legal, no mesmo grupo. Espelho de
 * `DOCUMENT_LIST_STATUSES` da API.
 */
export const STATUS_DO_FILTRO_DE_DOCUMENTOS = [
  "AVAILABLE",
  "QUARANTINED",
  "REJECTED",
  "LEGAL_HOLD",
] as const;
export type StatusDoFiltroDeDocumentos =
  (typeof STATUS_DO_FILTRO_DE_DOCUMENTOS)[number];
/**
 * TUT-204i/TUT-489a — o recorte pedido ao servidor, com os nomes do contrato
 * HTTP. Os filtros por coluna são listas (P30): a chave se repete na query.
 */
export interface RecorteDeDocumentos {
  category?: readonly string[];
  retentionClass?: readonly string[];
  processId?: readonly string[];
  status?: readonly StatusDoFiltroDeDocumentos[];
  confidentiality?: readonly string[];
  /** TUT-489b — as etiquetas marcadas no filtro (ids). */
  tag?: readonly string[];
  updatedWithinDays?: number;
}
/**
 * TUT-489c — o resultado de UM documento num lote: feito, ou o motivo de ter
 * ficado de fora. Um item recusado não desfaz os outros.
 */
export type ItemDoLote =
  | { documentId: string; titulo: string; feito: true }
  | { documentId: string; titulo: string; feito: false; motivo: string };
/** O que o .zip vai levar e o que fica de fora (D3: com aviso). */
export interface PreparoDoDownloadEmLote {
  /** `bytes`: o tamanho da versão atual, para o aviso de .zip grande. */
  liberados: { documentId: string; titulo: string; arquivo: string; bytes: number }[];
  deFora: { documentId: string; titulo: string; motivo: string }[];
}
export interface DocumentListResponse extends EcoDePaginacao {
  items: ProcessDocument[];
  total: number;
  summary: {
    total: number;
    quarantined: number;
    restricted: number;
  };
}
/**
 * Retenção por classe (TUT-044, D-04). Sem política — o padrão até a tabela
 * do cliente final chegar — nada entra em descarte e a lista vem vazia.
 */
export interface UpcomingDiscard {
  documentId: string;
  title: string;
  processNumber: string;
  retentionClass: "PROCESS_LIFECYCLE" | "LEGAL_HOLD" | "UNCLASSIFIED";
  discardAt: string;
  extendedDays: number;
}
/** Opções de extensão self-service (dias) — espelho da API. */
export const RETENTION_EXTENSION_OPTIONS = [30, 90, 180, 365] as const;
export interface UploadDocumentInput {
  file: File;
  title?: string;
  category?:
    | "PLEADING"
    | "EXPERT_REPORT"
    | "TECHNICAL_REPORT"
    | "EVIDENCE"
    | "CORRESPONDENCE"
    | "OTHER";
  retentionClass?: "PROCESS_LIFECYCLE" | "LEGAL_HOLD" | "UNCLASSIFIED";
  confidentiality?: "OPERATIONAL" | "RESTRICTED";
  documentId?: string;
}
